import { pluginVerifier } from '@features/dynamic-plugin'

import { sessionApi, SessionApi } from './SessionApi'

/**
 * Оркестрация сессии оверлея.
 *
 * ── Жизненный цикл ─────────────────────────────────────────────────────────────
 *   1. bootstrap(): достаём bootstrap-токен из URL (?token=...),
 *      обмениваем его на сессионную cookie (HttpOnly), затем грузим
 *      публичный ключ Ed25519 и передаём его в pluginVerifier.
 *      Токен из URL после успешного обмена удаляется из адресной строки
 *      (чтобы не светился в логах OBS / истории).
 *
 *   2. refreshSession(): вызывается транспортом при ошибке авторизации
 *      (cookie истекла). Делает POST /auth/overlay/refresh. Single-flight:
 *      параллельные вызовы переиспользуют один промис. При успехе ключ
 *      перезагружается (на случай ротации).
 *
 *   Публичный ключ имеет ЕДИНСТВЕННЫЙ источник — GET /auth/overlay/public-key.
 *   Локального env-ключа (VITE_PLUGIN_PUBLIC_KEY) больше нет.
 */
export class SessionService {
  private refreshInFlight: Promise<boolean> | null = null
  private publicKeyReloadInFlight: Promise<boolean> | null = null

  constructor(private readonly api: SessionApi = sessionApi) {}

  /**
   * Инициализация сессии при старте оверлея.
   * @returns true если сессия готова (cookie установлена и ключ загружен).
   */
  async bootstrap(): Promise<boolean> {
    const token = this.readTokenFromUrl()

    // Если токен есть — обмениваем на cookie. Если нет — возможно, cookie уже
    // установлена ранее (перезагрузка страницы) — пробуем сразу ключ.
    if (token) {
      const exchanged = await this.api.exchangeToken(token)
      if (exchanged) {
        this.stripTokenFromUrl()
      } else {
        // Токен невалиден/просрочен — пробуем refresh (вдруг cookie ещё жива)
        await this.refreshSession()
      }
    }

    return this.loadPublicKey()
  }

  /**
   * Продление сессии. Single-flight: одновременные вызовы ждут один и тот же
   * сетевой запрос. Используется транспортом как onAuthError.
   */
  refreshSession(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight

    this.refreshInFlight = (async () => {
      const ok = await this.api.refresh()
      if (ok) {
        // Ключ мог быть ротацией — перезагружаем (не критично, если упадёт)
        await this.loadPublicKey()
      }
      return ok
    })()

    void this.refreshInFlight.finally(() => {
      this.refreshInFlight = null
    })

    return this.refreshInFlight
  }

  /**
   * Перезагружает публичный ключ без полного refreshSession.
   * Используется DynamicPluginLoader когда verify падает из-за
   * keyId-mismatch (бэкенд рестартнул с новым ключом, а overlay
   * ещё держит старый). Single-flight: параллельные вызовы
   * переиспользуют один и тот же сетевой запрос (если сразу
   * несколько плагинов споткнулись о невалидную подпись).
   */
  reloadPublicKey(): Promise<boolean> {
    if (this.publicKeyReloadInFlight) return this.publicKeyReloadInFlight

    this.publicKeyReloadInFlight = this.loadPublicKey().finally(() => {
      this.publicKeyReloadInFlight = null
    })

    return this.publicKeyReloadInFlight
  }

  // ── Внутреннее ──────────────────────────────────────────────────────────────

  /** Загружает публичный ключ + keyId и передаёт их в верификатор плагинов. */
  private async loadPublicKey(): Promise<boolean> {
    const resp = await this.api.fetchPublicKey()
    if (!resp?.publicKey || !resp.keyId) return false
    pluginVerifier.setPublicKey(resp.publicKey, resp.keyId)
    return true
  }

  private readTokenFromUrl(): string | null {
    try {
      const url = new URL(window.location.href)
      // Поддерживаем и query (?token=), и hash (#token=) на всякий случай
      const fromQuery = url.searchParams.get('token')
      if (fromQuery) return fromQuery
      if (url.hash) {
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
        return hashParams.get('token')
      }
      return null
    } catch {
      return null
    }
  }

  /** Убирает token из адресной строки без перезагрузки страницы. */
  private stripTokenFromUrl(): void {
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      if (url.hash) {
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
        hashParams.delete('token')
        url.hash = hashParams.toString() ? `#${hashParams.toString()}` : ''
      }
      window.history.replaceState({}, '', url.toString())
    } catch {
      // игнорируем
    }
  }
}

export const sessionService = new SessionService()
